import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { isClerkEnabled } from '../hooks/useClerkSafe'
import { motion } from 'framer-motion'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts'
import { fetchSessions, fetchStats } from '../api/client'
import type { Session, Stats } from '../types'
import { surahName } from '../data/surahs'
import clsx from 'clsx'
import { DashboardSkeleton } from '../components/Skeleton'
import AchievementsGrid from '../components/Achievements'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
}

function AnimatedNumber({ value, duration = 1000 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0)
  const ref = useRef<number>(0)

  useEffect(() => {
    const start = ref.current
    const diff = value - start
    const startTime = performance.now()

    function tick(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = start + diff * eased
      setDisplay(Math.round(current))
      ref.current = current
      if (progress < 1) {
        requestAnimationFrame(tick)
      }
    }

    requestAnimationFrame(tick)
  }, [value, duration])

  return <>{display}</>
}

export default function DashboardPage() {
  const clerkUser = isClerkEnabled ? useUser() : { user: null }
  const user = clerkUser.user
  const navigate = useNavigate()
  const PAGE_SIZE = 5
  const [sessions, setSessions] = useState<Session[]>([])
  const [sessionsTotal, setSessionsTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  // Initial load: fetch first page of sessions + stats
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [sessionsData, statsData] = await Promise.all([
          fetchSessions(PAGE_SIZE, 0),
          fetchStats(),
        ])
        if (!cancelled) {
          setSessions(sessionsData.items)
          setSessionsTotal(sessionsData.total)
          setStats(statsData)
        }
      } catch {
        // API not available yet, use empty state
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  // Refetch sessions when page changes (skip page 0 — initial load already covered it)
  useEffect(() => {
    if (page === 0) return
    let cancelled = false
    fetchSessions(PAGE_SIZE, page * PAGE_SIZE)
      .then((data) => {
        if (!cancelled) {
          setSessions(data.items)
          setSessionsTotal(data.total)
        }
      })
      .catch(() => { /* silent */ })
    return () => { cancelled = true }
  }, [page])

  // Stats counts the FULL dataset across all pages — never derive from
  // the paginated `sessions` slice, or numbers will shrink as you page.
  const totalSessions = stats?.totalSessions ?? sessionsTotal
  const averageScore = stats?.averageScore ?? (sessions.length > 0
    ? Math.round(sessions.reduce((s, x) => s + x.overallScore, 0) / sessions.length)
    : 0)

  // Calculate streak (consecutive days with sessions)
  const streak = (() => {
    if (sessions.length === 0) return 0
    const dates = [...new Set(sessions.map(s => new Date(s.createdAt).toDateString()))]
    let count = 1
    const today = new Date().toDateString()
    if (dates[0] !== today) return 0
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1])
      const curr = new Date(dates[i])
      prev.setDate(prev.getDate() - 1)
      if (prev.toDateString() === curr.toDateString()) {
        count++
      } else {
        break
      }
    }
    return count
  })()

  // Rules mastered (rules with 0 findings)
  const findingsByRule = stats?.findingsCountByRule ?? {}
  const allRules = ['madd', 'ghunnah', 'idgham', 'ikhfa', 'iqlab', 'qalqalah']
  const rulesMastered = allRules.filter(r => !(r in findingsByRule) || findingsByRule[r] === 0).length

  const chartData = [...sessions].reverse().map((s, i) => ({
    name: `#${i + 1}`,
    score: s.overallScore,
    date: new Date(s.createdAt).toLocaleDateString(),
  }))

  const statCards = [
    {
      label: 'Total Sessions',
      value: totalSessions,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
        </svg>
      ),
    },
    {
      label: 'Average Score',
      value: averageScore,
      color: averageScore >= 80 ? 'text-emerald-400' : averageScore >= 60 ? 'text-yellow-400' : 'text-red-400',
      bg: averageScore >= 80 ? 'bg-emerald-500/10' : averageScore >= 60 ? 'bg-yellow-500/10' : 'bg-red-500/10',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ),
    },
    {
      label: 'Current Streak',
      value: streak,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
        </svg>
      ),
    },
    {
      label: 'Rules Mastered',
      value: rulesMastered,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
        </svg>
      ),
    },
  ]

  if (loading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Assalamu Alaikum, {user?.firstName || 'Student'}
        </h1>
        <p className="text-gray-500 dark:text-gray-500 dark:text-gray-400 mt-1">Here is your recitation progress overview.</p>
      </motion.div>

      {/* Quick Actions (moved to top) */}
      <motion.div
        className="flex flex-col sm:flex-row items-center gap-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        <Link to="/practice" className="btn-primary text-base px-8 py-3">
          Start Practice
        </Link>
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Coming soon"
          className="inline-flex items-center gap-2 rounded-xl bg-gray-200 dark:bg-gray-700 px-8 py-3 text-base font-semibold text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-70"
        >
          🎓 Live Session <span className="text-xs font-normal">(coming soon)</span>
        </button>
      </motion.div>

      {/* Stats Row */}
      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {statCards.map((card) => (
          <motion.div
            key={card.label}
            className="glass-card p-5"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', card.bg, card.color)}>
                {card.icon}
              </div>
            </div>
            <p className={clsx('text-3xl font-bold', card.color)}>
              <AnimatedNumber value={card.value} />
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.label}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Row */}
      {/* Score Trend */}
      <motion.div
        className="glass-card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wider">Score Trend</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip
                contentStyle={{ background: 'var(--tooltip-bg, #ffffff)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '0.75rem', fontSize: 13 }}
                labelStyle={{ color: '#9ca3af' }}
                itemStyle={{ color: '#10b981' }}
              />
              <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[220px] text-gray-500 text-sm">
            No sessions yet. Start practicing to see your progress!
          </div>
        )}
      </motion.div>

      {/* Recent Sessions */}
      <motion.div
        className="glass-card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recent Sessions</h3>
          {sessionsTotal > PAGE_SIZE && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sessionsTotal)} of {sessionsTotal}
            </span>
          )}
        </div>
        {sessions.length > 0 ? (
          <>
            <div className="space-y-2">
              {sessions.map((session, idx) => {
                const scoreColor = session.overallScore >= 80
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : session.overallScore >= 60
                  ? 'text-yellow-400 bg-yellow-500/10'
                  : 'text-red-400 bg-red-500/10'
                return (
                  <div
                    key={session.sessionId || idx}
                    onClick={() => navigate(`/practice?surah=${session.surah}&ayah=${session.ayah}`)}
                    className="flex items-center justify-between bg-gray-100 dark:bg-gray-800/30 rounded-xl px-4 py-3 hover:bg-gray-200 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-200 font-medium truncate">
                        Surah {surahName(session.surah)}, Ayah {session.ayah}
                      </span>
                      <span className="text-xs text-gray-500 shrink-0">
                        {new Date(session.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    <span className={clsx('text-sm font-bold px-3 py-1 rounded-lg', scoreColor)}>
                      {Math.round(session.overallScore)}
                    </span>
                  </div>
                )
              })}
            </div>
            {sessionsTotal > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(sessionsTotal / PAGE_SIZE))}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= sessionsTotal}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No sessions recorded yet. Start your first practice session!
          </div>
        )}
      </motion.div>

      {/* Surah Progress */}
      {sessions.length > 0 && (
        <motion.div
          className="glass-card p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.55 }}
        >
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Surah Progress</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              const surahMap: Record<number, { name: string; practiced: Set<number>; total: number }> = {}
              for (const s of sessions) {
                if (!surahMap[s.surah]) {
                  surahMap[s.surah] = { name: `Surah ${s.surah}`, practiced: new Set(), total: 0 }
                }
                surahMap[s.surah].practiced.add(s.ayah)
                surahMap[s.surah].total = Math.max(surahMap[s.surah].total, s.ayah)
              }
              return Object.entries(surahMap).slice(0, 8).map(([num, data]) => {
                const pct = Math.min(100, (data.practiced.size / Math.max(data.total, 1)) * 100)
                return (
                  <div key={num} className="bg-gray-50 dark:bg-gray-800/30 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{data.name}</span>
                      <span className="text-xs text-gray-500">{data.practiced.size} ayahs</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </motion.div>
      )}

      {/* Achievements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <AchievementsGrid sessions={sessions} streak={streak} />
      </motion.div>

    </div>
  )
}
